import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Polygon, CircleMarker, Tooltip, Marker, useMap } from "react-leaflet";
import { divIcon, latLngBounds } from "leaflet";
import "leaflet/dist/leaflet.css";
import { Phone } from "lucide-react";
import { Reveal } from "./Reveal";
import { ZONES, FEE_META, ZONES_GEO } from "../lib/zones";
import { PHONE_DISPLAY, PHONE_HREF } from "../lib/site";

const TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const FULL_BOUNDS = [[44.58, -93.72], [45.32, -92.7]];

const zoneLabelIcon = (num, active) =>
  divIcon({
    className: "zone-num-label",
    html: `<span style="font-family:'Clash Display',sans-serif;font-weight:700;font-size:${active ? 44 : 30}px;color:${active ? "rgba(255,90,0,0.9)" : "rgba(255,255,255,0.22)"};text-shadow:0 2px 14px rgba(0,0,0,0.7)">${num}</span>`,
    iconSize: [0, 0],
  });

const MapController = ({ selectedId }) => {
  const map = useMap();
  useEffect(() => {
    if (selectedId === "all") {
      map.flyToBounds(FULL_BOUNDS, { padding: [20, 20], duration: 0.8 });
      return;
    }
    const geo = ZONES_GEO[selectedId];
    if (geo) map.flyToBounds(latLngBounds(geo.polygon), { padding: [28, 28], duration: 0.8 });
  }, [selectedId, map]);
  return null;
};

const sortCities = (cities) =>
  [...cities].sort((a, b) => (a.startsWith("&") ? 1 : b.startsWith("&") ? -1 : a.localeCompare(b)));

export const ZoneMap = () => {
  const [selectedId, setSelectedId] = useState("all");
  const [hoverId, setHoverId] = useState(null);
  const isAll = selectedId === "all";
  const selected = ZONES.find((z) => z.id === selectedId);
  const meta = selected ? FEE_META[selected.fee] : null;

  return (
    <section id="service-area" data-testid="service-area-section" className="py-24 md:py-32 border-t border-white/10 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="eyebrow mb-4 text-center">02 — Service Area</p>
          <h2 className="font-display font-bold uppercase tracking-normal text-4xl sm:text-5xl lg:text-6xl max-w-3xl mx-auto leading-[1.02] text-center">
            <span className="block">Seven zones.</span>
            <span className="block">Zero guesswork.</span>
          </h2>
          <p className="mt-5 text-neutral-400 max-w-2xl mx-auto text-center">
            Zones 1–3 never pay a mileage fee. Zones 4–6 add $3/mile after the first 15 miles. Zone 7 gets an exact all-in quote before booking. Tap a zone to zoom in and see its cities.
          </p>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-[1.5fr_1fr] gap-8 items-start">
          <Reveal>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="zone-chips">
              <button
                data-testid="zone-chip-all"
                onClick={() => setSelectedId("all")}
                className={`font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-2 border transition-colors duration-200 ${
                  isAll ? "border-primary text-primary" : "border-white/15 text-neutral-400 hover:border-white/40"
                }`}
              >
                All Zones
              </button>
              {ZONES.map((z) => (
                <button
                  key={z.id}
                  data-testid={`zone-chip-${z.id}`}
                  onClick={() => setSelectedId(z.id)}
                  className={`font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-2 border transition-colors duration-200 ${
                    selectedId === z.id ? "border-primary text-primary" : "border-white/15 text-neutral-400 hover:border-white/40"
                  }`}
                >
                  Zone {z.num}
                </button>
              ))}
            </div>
            <div className="border border-white/10 bg-[#0A0A0A] overflow-hidden" data-testid="zone-map">
              <MapContainer bounds={FULL_BOUNDS} scrollWheelZoom={false} className="h-[420px] md:h-[560px] w-full z-0">
                <TileLayer url={TILE_URL} attribution={ATTRIBUTION} />
                <MapController selectedId={selectedId} />
                {ZONES.map((z) => {
                  const geo = ZONES_GEO[z.id];
                  const st = FEE_META[z.fee];
                  const active = selectedId === z.id || hoverId === z.id;
                  return (
                    <Polygon
                      key={z.id}
                      positions={geo.polygon}
                      pathOptions={{
                        color: st.stroke,
                        weight: active ? 2.5 : 1.25,
                        dashArray: st.dash,
                        fillColor: z.fee === "free" ? "#FF5A00" : "#FFFFFF",
                        fillOpacity: active ? (z.fee === "free" ? 0.22 : 0.12) : z.fee === "free" ? 0.06 : 0.03,
                      }}
                      eventHandlers={{
                        mouseover: () => setHoverId(z.id),
                        mouseout: () => setHoverId(null),
                        click: () => setSelectedId(z.id),
                      }}
                    />
                  );
                })}
                {ZONES.map((z) => (
                  <Marker
                    key={`label-${z.id}`}
                    position={ZONES_GEO[z.id].center}
                    icon={zoneLabelIcon(z.num, selectedId === z.id || hoverId === z.id)}
                    interactive={false}
                  />
                ))}
                {ZONES.filter((z) => z.id === selectedId || z.id === hoverId).flatMap((z) => {
                  const st = FEE_META[z.fee];
                  return Object.entries(ZONES_GEO[z.id].cities).map(([name, spec], i) => {
                    const dir = spec[2] || (i % 2 ? "left" : "right");
                    const offset = dir === "left" ? [-8, 0] : dir === "right" ? [8, 0] : dir === "top" ? [0, -8] : [0, 8];
                    return (
                      <CircleMarker
                        key={`${z.id}-${name}`}
                        center={spec}
                        radius={4}
                        pathOptions={{ color: st.dot, fillColor: st.dot, fillOpacity: 0.95, weight: 1 }}
                        eventHandlers={{ click: () => setSelectedId(z.id) }}
                      >
                        <Tooltip permanent direction={dir} offset={offset} className="city-label">
                          {name}
                        </Tooltip>
                      </CircleMarker>
                    );
                  });
                })}
              </MapContainer>
            </div>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="zone-legend">
              <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-400 border border-white/10 px-3 py-2">
                <span className="w-2.5 h-2.5 bg-primary inline-block" /> Zones 1–3 — no mileage fee
              </span>
              <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-400 border border-white/10 px-3 py-2">
                <span className="w-2.5 h-2.5 bg-neutral-300 inline-block" /> Zones 4–6 — $3/mi after 15 mi
              </span>
              <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-400 border border-white/10 px-3 py-2">
                <span className="w-2.5 h-2.5 bg-neutral-600 inline-block" /> Zone 7 — custom quote
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div data-testid="zone-panel" className="border border-white/10 bg-[#121212] p-6 md:p-8 lg:sticky lg:top-24">
              <div className="flex items-baseline justify-between gap-4">
                <h3 className="font-display font-bold uppercase tracking-normal text-3xl" data-testid="zone-panel-name">
                  {isAll ? "All Zones" : selected.name}
                </h3>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                  {isAll ? "Twin Cities Metro" : selected.region}
                </span>
              </div>
              <span
                data-testid="zone-panel-fee"
                className={`inline-block mt-4 font-mono text-[11px] uppercase tracking-[0.2em] border px-3 py-1.5 ${
                  isAll || !selected
                    ? "border-white/20 text-neutral-300"
                    : selected.fee === "free"
                      ? "border-primary/60 text-primary"
                      : selected.fee === "fee"
                        ? "border-white/30 text-neutral-200"
                        : "border-white/20 text-neutral-400"
                }`}
              >
                {isAll ? "1–3 free · 4–6 +$3/mi · 7 quoted" : meta.tag}
              </span>
              <p className="mt-5 text-sm text-neutral-400 leading-relaxed">
                {isAll
                  ? "Every city we serve, grouped into seven zones by distance from our Minneapolis home base. Pick a zone on the map to see its cities and pricing."
                  : selected.blurb}
              </p>

              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                {isAll ? "Select a zone" : `Cities & neighborhoods (${selected.cities.length})`}
              </p>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="zone-panel-cities">
                {isAll
                  ? ZONES.map((z) => (
                      <button
                        key={z.id}
                        data-testid={`panel-pick-${z.id}`}
                        onClick={() => setSelectedId(z.id)}
                        className="border border-white/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide text-neutral-300 transition-colors duration-200 hover:border-primary hover:text-primary"
                      >
                        {z.name} · {z.region}
                      </button>
                    ))
                  : sortCities(selected.cities).map((c) => (
                      <span key={c} className="border border-white/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide text-neutral-300">
                        {c}
                      </span>
                    ))}
              </div>

              <a data-testid="zone-panel-cta" href={PHONE_HREF} className="btn-primary w-full mt-8">
                <Phone size={15} /> {isAll ? `Call ${PHONE_DISPLAY}` : selected.fee === "quote" ? `Call ${PHONE_DISPLAY} for a quote` : `Call to book ${selected.name} — ${PHONE_DISPLAY}`}
              </a>
              <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                Exact price always confirmed before dispatch
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
